import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Circle, Rocket, X, Flame, Sparkles } from "lucide-react";


/**
 * Onboarding: welcome modal (once) + persistent checklist card on Dashboard.
 * Steps auto-derive from user data so nothing to "mark complete" manually.
 * Hides checklist entirely once all steps are done.
 */
export function OnboardingChecklist() {
  const [status, setStatus] = useState(null);
  const [showWelcome, setShowWelcome] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/onboarding/status");
      setStatus(data);
      // Show welcome modal on first visit ever if user hasn't dismissed it yet AND is a fresh user
      if (!data.welcomed && !data.all_done && data.progress_pct < 25) {
        setShowWelcome(true);
      }
    } catch (err) {
      console.debug("[onboarding] load failed", err);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const dismissWelcome = async () => {
    setShowWelcome(false);
    try { await api.post("/onboarding/dismiss-welcome"); } catch { /* silent */ }
  };

  if (!status || status.all_done) return null;

  const items = [
    { key: "broker_connected",       label: "Connect a broker",         href: "/broker",     testId: "onb-step-broker" },
    { key: "strategy_created",       label: "Forge your first strategy", href: "/builder",    testId: "onb-step-strategy" },
    { key: "deployed",               label: "Deploy in paper mode",     href: "/deployments", testId: "onb-step-deploy" },
    { key: "notifications_configured", label: "Set up notifications",   href: "/settings/notifications", testId: "onb-step-notif" },
  ];

  return (
    <>
      <section className="card-sharp p-5 mb-6" data-testid="onboarding-checklist">
        <div className="flex items-center gap-2 mb-3">
          <Rocket size={14} className="text-[var(--accent)]"/>
          <h3 className="font-display text-lg text-white flex-1">Get started</h3>
          <span className="chip">{status.progress_pct}%</span>
        </div>

        <div className="w-full h-1.5 bg-black/40 rounded mb-4 overflow-hidden">
          <div className="h-full bg-[var(--accent)] transition-all"
            style={{ width: `${status.progress_pct}%` }} data-testid="onboarding-progress"/>
        </div>

        <ul className="space-y-2">
          {items.map((it) => {
            const done = status.steps?.[it.key];
            return (
              <li key={it.key} data-testid={it.testId}
                className={`flex items-center gap-3 py-2 px-3 rounded border ${
                  done
                    ? "border-[var(--profit)]/30 bg-[var(--profit)]/5"
                    : "border-white/10 hover:border-[var(--accent)]/40 hover:bg-[var(--accent)]/5"
                }`}>
                {done
                  ? <CheckCircle2 size={16} className="text-[var(--profit)] shrink-0" data-testid={`${it.testId}-done`}/>
                  : <Circle size={16} className="text-[var(--text-muted)] shrink-0"/>}
                <span className={`text-sm flex-1 ${done ? "text-[var(--text-muted)] line-through" : "text-white"}`}>
                  {it.label}
                </span>
                {!done && (
                  <Link to={it.href}
                    className="text-[10px] font-mono text-[var(--accent)] hover:underline"
                    data-testid={`${it.testId}-cta`}>
                    Do it →
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      </section>

      {showWelcome && <WelcomeModal onDismiss={dismissWelcome} progress={status.progress_pct}/>}
    </>
  );
}


function WelcomeModal({ onDismiss, progress }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onDismiss}
      data-testid="onboarding-welcome-modal">
      <div
        className="card-sharp max-w-lg w-full p-8 relative"
        onClick={(e) => e.stopPropagation()}>
        <button data-testid="welcome-modal-close" onClick={onDismiss}
          className="absolute top-3 right-3 text-[var(--text-muted)] hover:text-white">
          <X size={16}/>
        </button>

        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={18} className="text-[var(--accent)]"/>
          <span className="chip">FORGE · WELCOME</span>
        </div>

        <h2 className="font-display text-3xl text-white mb-3 leading-tight">
          Welcome to MQL4Forge.
        </h2>
        <p className="text-sm text-[var(--text-secondary)] mb-6 leading-relaxed">
          Turn ideas into MetaTrader trading robots — visually or with AI. Meet <span className="text-[var(--accent)]">Forge</span>,
          your voice-enabled trading co-pilot, connect a broker to see live data, and deploy your first EA in paper mode without touching any code.
        </p>

        <div className="space-y-3 mb-6">
          <div className="flex items-start gap-3">
            <Flame size={16} className="text-[var(--accent)] mt-0.5 shrink-0"/>
            <div>
              <p className="text-sm text-white font-display">4-step launchpad</p>
              <p className="text-[11px] font-mono text-[var(--text-muted)]">
                Connect broker · Build strategy · Deploy · Configure alerts.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Sparkles size={16} className="text-[var(--accent)] mt-0.5 shrink-0"/>
            <div>
              <p className="text-sm text-white font-display">Everything is reversible</p>
              <p className="text-[11px] font-mono text-[var(--text-muted)]">
                Live trading is OFF by default. Everything defaults to paper mode.
              </p>
            </div>
          </div>
        </div>

        <button data-testid="welcome-modal-cta" onClick={onDismiss}
          className="btn-primary w-full py-3">
          Let&apos;s go {progress > 0 && `(${progress}% done)`}
        </button>
      </div>
    </div>
  );
}
